// v3.0.0-rc.7-phaseCD-CD_WALLET_RICH_PURCHASE_V1 — Phase D.2 · RichResponseRenderer
//
// Rend les ui_blocks structurés produits par orchestrator.js (Phase D.1).
// Schema attendu (cf. 03_phase_d1_backend.md §2.3) :
//   ui_blocks = [
//     { type: 'header', text: '3 commerces trouvés' },
//     { type: 'merchant_card', id, name, category, location, address, specialty,
//       rating, price_min_fcfa, price_max_fcfa, image_url, action: { label, action_id, params } },
//     ...
//   ]
//
// Palette : pêche/orange chaud aligné LiveBanner (cohérence écosystème chat),
// avec accent gold pour le CTA "Choisir" (rappel Wallet B2C).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// v3.0.0-rc.8e — render inline OrderTrackingLive quand LLM émet un order_tracking_ref
import OrderTrackingLive from "@/components/OrderTrackingLive";
import { openWhatsAppOrder } from "@/lib/whatsappOrder";
// [H_UIPOLISH_V1_L3 sous-palier 2.C] Polish-only : hover scale spring sur cartes commerçants.
// LazyMotion est monté globalement dans App.js → `m.div` est disponible.
import { m } from "framer-motion";
// [H_UIPOLISH_V1_L4] Polish-only : hero illustration sur cartes merchant.
import AyaAsset from "@/components/ui/AyaAsset";

// v3.0.0-rc.8z-ux — Palette unifiée émeraude foncé + champagne gold (retire l'ancienne beige/orange).
const C = {
  cardBg: "linear-gradient(145deg, rgba(11, 69, 49, 0.55), rgba(6, 42, 31, 0.55))",
  cardBorder: "rgba(232, 213, 163, 0.32)",
  cardShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
  ink: "#F4F1E8",
  inkDim: "rgba(244, 241, 232, 0.66)",
  inkFade: "rgba(244, 241, 232, 0.42)",
  accent: "#EBD9A8",
  gold: "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)",
  emeraldDeep: "#3A2E10",
  star: "#EBD9A8",
  imgPlaceholderBg: "rgba(232, 213, 163, 0.14)",
};

function _fmtPrice(min, max) {
  const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n);
  if (Number.isFinite(min) && Number.isFinite(max) && min !== max) return `${fmt(min)} – ${fmt(max)} FCFA`;
  if (Number.isFinite(min)) return `À partir de ${fmt(min)} FCFA`;
  return "—";
}

function _stars(r) {
  if (!Number.isFinite(r)) return null;
  const full = Math.round(r);
  return "★".repeat(full) + "☆".repeat(Math.max(0, 5 - full));
}

function _categoryIcon(cat) {
  const map = {
    shawarma: "🥙", attieke: "🍛", pharmacy: "💊", supermarche: "🛒",
    boulangerie: "🥐", restaurant: "🍽️", taxi: "🚖", coiffure: "💇",
  };
  return map[String(cat || "").toLowerCase()] || "🏬";
}

// [SPRINT K P2] ServiceProviderCard — carte prestataire (services à domicile).
// Modèle calqué sur MerchantCard. Click → /services/provider/:slug.
function ServiceProviderCard({ block }) {
  const navigate = useNavigate();
  const slug = block.slug || block.id || null;
  const goToProvider = (e) => {
    if (!slug) return;
    e.stopPropagation();
    navigate(`/services/provider/${encodeURIComponent(slug)}`);
  };
  const specialties = Array.isArray(block.specialties) ? block.specialties.slice(0, 3) : [];
  return (
    <m.div
      data-testid={`rich-service-provider-card-${block.id}`}
      className="aya-hover-lift"
      whileHover={{ scale: 1.02, y: -2 }}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
      onClick={slug ? goToProvider : undefined}
      role={slug ? "link" : undefined}
      tabIndex={slug ? 0 : undefined}
      onKeyDown={slug ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToProvider(e); } } : undefined}
      aria-label={slug ? `Voir le profil de ${block.name}` : undefined}
      style={{
        background: C.cardBg, border: `1px solid ${C.cardBorder}`,
        borderRadius: 18, padding: 14, marginTop: 10,
        boxShadow: `${C.cardShadow}, var(--aya-shadow-glow-gold)`,
        display: "flex", gap: 12, alignItems: "stretch",
        backdropFilter: "blur(var(--aya-glass-blur-sm)) saturate(var(--aya-glass-saturate))",
        WebkitBackdropFilter: "blur(var(--aya-glass-blur-sm)) saturate(var(--aya-glass-saturate))",
        cursor: slug ? "pointer" : "default",
      }}
    >
      <div style={{
        width: 64, height: 64, flexShrink: 0,
        borderRadius: 14, background: C.imgPlaceholderBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 30, color: C.accent,
        backgroundImage: block.photo ? `url(${block.photo})` : "none",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        {block.photo ? null : (block.icon || "🛠️")}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>{block.name}</div>
        <div style={{ fontSize: 11, color: C.inkDim, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {block.role && <span>{block.role}</span>}
          {block.location && <span>📍 {block.location}</span>}
          {block.rating != null && <span style={{ color: C.star }}>{_stars(block.rating)} <span style={{ color: C.inkDim }}>{Number(block.rating).toFixed(1)}</span></span>}
          {Number.isFinite(block.jobs_count) && <span style={{ color: C.inkFade }}>· {block.jobs_count} jobs</span>}
        </div>
        {specialties.length > 0 && (
          <div style={{ fontSize: 11, color: C.inkDim, display: "flex", flexWrap: "wrap", gap: 4 }}>
            {specialties.map((s, i) => (
              <span key={i} style={{ background: "rgba(212,175,55,0.16)", color: C.accent, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>{s}</span>
            ))}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4, gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, fontFeatureSettings: "'tnum'" }}>
            {_fmtPrice(block.price_min_fcfa, block.price_max_fcfa)}
          </div>
          <span
            data-testid={`rich-service-provider-cta-${block.id}`}
            style={{
              padding: "7px 14px", borderRadius: 999,
              background: C.gold, color: C.emeraldDeep,
              fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
              boxShadow: "0 3px 8px rgba(212,175,55,0.36)",
            }}
          >
            Voir profil →
          </span>
        </div>
      </div>
    </m.div>
  );
}

function MerchantCard({ block, onSelect, disabled }) {
  const action = block.action || {};
  const navigate = useNavigate();
  // [SPRINT J P1] Détermine le slug pour navigation page commerçant dédiée.
  const merchantSlug = block.slug || block.id || null;
  const goToMerchantPage = (e) => {
    if (!merchantSlug) return;
    e.stopPropagation();
    navigate(`/commercant/${encodeURIComponent(merchantSlug)}`);
  };
  return (
    <m.div
      data-testid={`rich-merchant-card-${block.id}`}
      className="aya-hover-lift"
      whileHover={{ scale: 1.02, y: -2 }}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
      onClick={merchantSlug ? goToMerchantPage : undefined}
      role={merchantSlug ? "link" : undefined}
      tabIndex={merchantSlug ? 0 : undefined}
      onKeyDown={merchantSlug ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToMerchantPage(e); } } : undefined}
      aria-label={merchantSlug ? `Voir la page de ${block.name}` : undefined}
      style={{
        background: C.cardBg, border: `1px solid ${C.cardBorder}`,
        borderRadius: 18, padding: 14, marginTop: 10,
        boxShadow: `${C.cardShadow}, var(--aya-shadow-glow-gold)`,
        display: "flex", gap: 12, alignItems: "stretch",
        backdropFilter: "blur(var(--aya-glass-blur-sm)) saturate(var(--aya-glass-saturate))",
        WebkitBackdropFilter: "blur(var(--aya-glass-blur-sm)) saturate(var(--aya-glass-saturate))",
        cursor: merchantSlug ? "pointer" : "default",
      }}
    >
      {/* Image (placeholder si absente) */}
      <div style={{
        width: 64, height: 64, flexShrink: 0,
        borderRadius: 14, background: C.imgPlaceholderBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 30, color: C.accent,
        backgroundImage: block.image_url ? `url(${block.image_url})` : "none",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        {block.image_url ? null : _categoryIcon(block.category)}
      </div>
      {/* Body */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>{block.name}</div>
        <div style={{ fontSize: 11, color: C.inkDim, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {block.location && <span>📍 {block.location}</span>}
          {block.rating != null && <span style={{ color: C.star }}>{_stars(block.rating)} <span style={{ color: C.inkDim }}>{block.rating.toFixed(1)}</span></span>}
        </div>
        {block.specialty && (
          <div style={{ fontSize: 12, color: C.inkDim, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {block.specialty}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4, gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, fontFeatureSettings: "'tnum'" }}>
            {_fmtPrice(block.price_min_fcfa, block.price_max_fcfa)}
          </div>
          <button
            type="button"
            data-testid={`rich-merchant-cta-${block.id}`}
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); onSelect && onSelect(action, block); }}
            style={{
              padding: "7px 14px", borderRadius: 999,
              background: disabled ? "rgba(212,175,55,0.4)" : C.gold,
              color: C.emeraldDeep, border: "none",
              fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
              letterSpacing: 0.3,
              boxShadow: disabled ? "none" : "0 3px 8px rgba(212,175,55,0.36)",
            }}
          >
            {action.label || "Choisir"} →
          </button>
        </div>
      </div>
    </m.div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// 2026-07-03 (Décision 25ᵉ) — ProductCard COMPACTE pour carrousel horizontal.
// v3.0.0-rc.8e — Ajout icônes 👁 (fiche produit modale) + ➕ (ajout panier local)
// en overlay top-right sur chaque carte.
// ═════════════════════════════════════════════════════════════════════════════
import { addItem as addToLocalCart } from "@/lib/localCartStore";
import ProductDetailModal from "@/components/ProductDetailModal";
import { subscribe as subStreamStore, getState as getStreamStoreState } from "@/lib/cartStreamStore";

function ProductCard({ block, onAction, disabled, onOpenDetail }) {
  const actions = Array.isArray(block.actions) ? block.actions : [];
  const orderAction = actions.find((a) => a.action_id === "order_product");
  const shopAction = actions.find((a) => a.action_id === "view_shop");
  const photos = Array.isArray(block.photos) ? block.photos.filter(Boolean) : [];
  const [photoIdx, setPhotoIdx] = useState(0);
  const [addedFlash, setAddedFlash] = useState(false);
  // v3.0.0-rc.8f — Realtime stock : le cartStreamStore accumule les product_id
  // passés en rupture via SSE. Une carte visible se fade instantanément.
  const productId = block.id || block.product_id;
  const [isDepleted, setIsDepleted] = useState(() => getStreamStoreState().depleted.has(productId));
  useEffect(() => {
    const apply = (s) => setIsDepleted(s.depleted.has(productId));
    apply(getStreamStoreState());
    const off = subStreamStore(apply);
    return () => { try { off && off(); } catch (_) { /* noop */ } };
  }, [productId]);
  const effectivelyAvailable = block.available && !isDepleted;

  const isPartner = block.provider === "partner";
  const etaLabel = Number.isFinite(block.delivery_eta_min)
    ? (block.delivery_eta_min >= 720 ? `${Math.max(1, Math.round(block.delivery_eta_min / 1440))}-2 j` : `~${block.delivery_eta_min} min`)
    : null;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (!effectivelyAvailable) return;
    addToLocalCart({
      product_id: productId,
      name: block.name,
      price_fcfa: block.price_fcfa,
      merchant_id: block.merchant_id,
      merchant_name: block.merchant_name,
      photos, emoji: block.emoji,
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 900);
  };

  return (
    <m.div
      data-testid="product-card"
      style={{
        flex: "0 0 235px",
        width: 235,
        /* v3.0.0-rc.8z-ux — Cartes produits TRANSPARENTES (request user 2026-08) : blend avec le chat. */
        background: "transparent",
        border: "1px solid rgba(232, 213, 163, 0.22)",
        borderRadius: 16,
        overflow: "hidden",
        color: "#F4F1E8",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        opacity: isDepleted ? 0.55 : 1,
        filter: isDepleted ? "grayscale(0.4)" : "none",
        transition: "opacity 300ms, filter 300ms",
      }}
    >
      <div style={{ position: "relative", width: "100%", height: 138, background: "#04180F", flexShrink: 0 }}>
        {photos.length > 0 ? (
          <img src={photos[photoIdx % photos.length]} alt={block.name} loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 42 }}>{block.emoji || "🛍"}</div>
        )}
        {block.sponsored && (
          <span style={{ position: "absolute", top: 8, left: 8, fontSize: 8, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", padding: "3px 8px", borderRadius: 999, background: "rgba(6,42,31,0.85)", color: "#EBD9A8", border: "1px solid rgba(232,213,163,0.5)" }}>
            Sponsorisé
          </span>
        )}
        {isDepleted && (
          <div
            data-testid={`product-oos-ribbon-${productId}`}
            style={{
              position: "absolute", top: 50, left: 0, right: 0,
              background: "rgba(180, 30, 30, 0.85)",
              color: "#fff",
              padding: "4px 8px",
              fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
              textAlign: "center", textTransform: "uppercase",
              boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
              backdropFilter: "blur(3px)",
            }}
          >Bientôt de retour</div>
        )}
        {photos.length > 1 && (
          <button type="button" aria-label="Photo suivante"
            onClick={(e) => { e.stopPropagation(); setPhotoIdx((i) => (i + 1) % photos.length); }}
            style={{ position: "absolute", right: 6, bottom: 6, width: 24, height: 24, borderRadius: "50%", border: "none", background: "rgba(6,42,31,0.8)", color: "#F4F1E8", cursor: "pointer", fontSize: 12 }}>›</button>
        )}
        {/* v3.0.0-rc.8e — Actions overlay top-right : 👁 fiche + ➕ panier */}
        <div style={{
          position: "absolute", top: 8, right: 8,
          display: "flex", gap: 6,
        }}>
          <button
            type="button"
            data-testid={`product-eye-${block.id}`}
            aria-label="Voir la fiche produit"
            onClick={(e) => { e.stopPropagation(); onOpenDetail && onOpenDetail(); }}
            style={{
              width: 30, height: 30, borderRadius: "50%",
              background: "rgba(6,42,31,0.85)", color: "#F4F1E8",
              border: "1px solid rgba(232,213,163,0.4)",
              cursor: "pointer", fontSize: 14, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(3px)",
            }}
          >👁</button>
          <button
            type="button"
            data-testid={`product-add-${block.id}`}
            aria-label="Ajouter au panier"
            disabled={disabled || !effectivelyAvailable}
            onClick={handleAdd}
            style={{
              width: 30, height: 30, borderRadius: "50%",
              background: addedFlash
                ? "#10B981"
                : (effectivelyAvailable ? "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)" : "rgba(244,241,232,0.15)"),
              color: addedFlash ? "#fff" : (effectivelyAvailable ? "#3A2E10" : "rgba(244,241,232,0.4)"),
              border: "none",
              cursor: effectivelyAvailable ? "pointer" : "default",
              fontSize: 16, fontWeight: 900, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
              transition: "background 200ms, transform 120ms",
              transform: addedFlash ? "scale(1.15)" : "scale(1)",
            }}
          >{addedFlash ? "✓" : "＋"}</button>
        </div>
      </div>

      <div style={{ padding: "9px 10px 11px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.25, minHeight: 31 }}>{block.name}</div>
        <div style={{ fontSize: 10, color: "rgba(244,241,232,0.55)", marginTop: 2, minHeight: 13 }}>
          {block.brand ? block.brand : ""}
          {Array.isArray(block.sizes) && block.sizes.length > 0 ? `${block.brand ? " · " : ""}T. ${block.sizes.slice(0, 4).join(",")}` : ""}
        </div>
        {Number.isFinite(block.rating) && (
          <div style={{ fontSize: 10.5, marginTop: 2 }}>
            <span style={{ color: C.star }}>★ {block.rating}</span>
            {Number.isFinite(block.reviews_count) && <span style={{ color: "#34D399" }}> ({block.reviews_count})</span>}
          </div>
        )}
        <div style={{ fontSize: 15.5, fontWeight: 800, color: "#EBD9A8", margin: "4px 0 2px", fontFeatureSettings: "'tnum'" }}>
          {new Intl.NumberFormat("fr-FR").format(block.price_fcfa)} FCFA{block.price_suffix || ""}
        </div>
        <div style={{ fontSize: 9.5, color: "rgba(244,241,232,0.65)", lineHeight: 1.4 }}>
          {isPartner ? `Vendu par ${block.sold_by || block.merchant_name}` : `🏪 ${block.merchant_name}`}
          {block.merchant_location ? ` · ${block.merchant_location}` : ""}
          {typeof block.merchant_online === "boolean" && (
            block.merchant_online
              ? <span style={{ color: "#34D399", fontWeight: 700 }}> · 🟢 En ligne</span>
              : <span style={{ color: "rgba(244,241,232,0.45)", fontWeight: 700 }}> · ⚪ Hors ligne</span>
          )}
          <br />
          {block.available ? <span style={{ color: "#34D399" }}>● En stock</span> : <span style={{ color: "#F87171" }}>● Rupture</span>}
          {isDepleted && <span style={{ color: "#F87171" }}> · ⏳ Bientôt de retour</span>}
          {etaLabel ? ` · 🛵 ${etaLabel}` : ""}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: "auto", paddingTop: 8 }}>
          {orderAction && (
            <button
              type="button"
              data-testid="product-order-btn"
              disabled={disabled || !effectivelyAvailable}
              onClick={(e) => { e.stopPropagation(); onAction && onAction(orderAction, block); }}
              style={{
                flex: 1.5, padding: "8px 8px", borderRadius: 9, border: "none",
                background: effectivelyAvailable ? "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)" : "rgba(244,241,232,0.15)",
                color: effectivelyAvailable ? "#3A2E10" : "rgba(244,241,232,0.4)",
                fontWeight: 800, fontSize: 11.5, cursor: effectivelyAvailable ? "pointer" : "default",
              }}
            >
              Ajouter au panier
            </button>
          )}
          {block.merchant_phone && (
            <button
              type="button"
              data-testid="product-whatsapp-btn"
              disabled={disabled}
              aria-label="Commander sur WhatsApp"
              title="Commander sur WhatsApp"
              onClick={(e) => {
                e.stopPropagation();
                openWhatsAppOrder({
                  merchantId: block.merchant_id,
                  productId: block.id || block.product_id,
                  productName: block.name,
                  priceFcfa: block.price_fcfa,
                });
              }}
              style={{
                flex: 1, padding: "8px 4px", borderRadius: 9,
                border: "1px solid rgba(37,211,102,0.5)", background: "rgba(37,211,102,0.12)",
                color: "#25D366", fontWeight: 800, fontSize: 10, cursor: "pointer", whiteSpace: "nowrap",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </button>
          )}
          {shopAction && (
            <button
              type="button"
              data-testid="product-shop-btn"
              disabled={disabled}
              aria-label="Voir le produit"
              onClick={(e) => { e.stopPropagation(); onAction && onAction(shopAction, block); }}
              style={{
                flex: 1, padding: "8px 6px", borderRadius: 9,
                border: "1px solid rgba(232,213,163,0.4)", background: "rgba(232,213,163,0.08)",
                color: "#EBD9A8", fontWeight: 700, fontSize: 11, cursor: "pointer",
              }}
            >
              👁 Voir
            </button>
          )}
        </div>
      </div>
    </m.div>
  );
}

export default function RichResponseRenderer({ ui_blocks, onMerchantSelect, onProductAction, onQuickPrompt, disabled = false }) {
  const blocks = useMemo(() => (Array.isArray(ui_blocks) ? ui_blocks : []), [ui_blocks]);

  // v3.0.0-rc.8n — Modal produit MUTUALISÉE : un seul modal pour tout le carrousel,
  // avec navigation swipe/‹›/← → + mémoire de position (scroll retour sur la carte visitée).
  const [detailIndex, setDetailIndex] = useState(-1);
  const carouselRef = useRef(null);
  const productBlocks = useMemo(() => blocks.filter((b) => b && b.type === "product_card"), [blocks]);

  // Sur fermeture, scroll le carrousel jusqu'à la carte visitée (position memory).
  const closeDetail = useCallback(() => {
    const idxToRestore = detailIndex;
    setDetailIndex(-1);
    setTimeout(() => {
      try {
        const el = carouselRef.current;
        if (!el || idxToRestore < 0) return;
        const slides = el.querySelectorAll(".aya-product-slide");
        const target = slides[idxToRestore];
        if (target) {
          const targetLeft = target.offsetLeft - el.offsetLeft - 12;
          el.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
        }
      } catch (_) { /* noop */ }
    }, 50);
  }, [detailIndex]);

  const currentProductPack = useMemo(() => {
    if (detailIndex < 0 || detailIndex >= productBlocks.length) return null;
    return productBlocks.map((b) => ({
      ...b,
      product_id: b.id || b.product_id,
      photos: Array.isArray(b.photos) ? b.photos.filter(Boolean) : [],
    }));
  }, [detailIndex, productBlocks]);

  if (blocks.length === 0) return null;

  const ctaBlocks = blocks.filter((b) => b && b.type === "cta_row");
  const otherBlocks = blocks.filter((b) => b && b.type !== "product_card" && b.type !== "cta_row");

  return (
    <div data-testid="rich-response-renderer" style={{ marginTop: 10 }}>
      {otherBlocks.map((b, idx) => {
        if (!b || typeof b !== "object") return null;
        if (b.type === "header") {
          return (
            <div
              key={`hdr-${idx}`}
              data-testid="rich-header"
              style={{ fontSize: 13, fontWeight: 700, color: C.accent, letterSpacing: 0.4, textTransform: "uppercase", marginTop: 6 }}
            >
              {b.text}
            </div>
          );
        }
        if (b.type === "merchant_card") {
          return (
            <MerchantCard
              key={b.id || `mc-${idx}`}
              block={b}
              onSelect={onMerchantSelect}
              disabled={disabled}
            />
          );
        }
        // v4.8.0 — Sources Web (Brave) : liens cliquables sous la réponse de l'agent.
        if (b.type === "web_sources" && Array.isArray(b.links) && b.links.length > 0) {
          return (
            <div key={`ws-${idx}`} data-testid="web-sources-block" style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.55, marginBottom: 6 }}>
                🌐 Sources Web
              </div>
              {b.links.map((l, i) => (
                <a
                  key={`wsl-${i}`}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`web-source-link-${i}`}
                  style={{
                    display: "block", padding: "8px 11px", marginBottom: 6, borderRadius: 10,
                    border: "1px solid rgba(230,126,34,0.35)", background: "rgba(230,126,34,0.07)",
                    textDecoration: "none", color: "inherit",
                  }}
                >
                  <span style={{ display: "block", fontSize: 10, color: "#E67E22", fontWeight: 700 }}>{l.domain}</span>
                  <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>{l.title}</span>
                </a>
              ))}
            </div>
          );
        }
        // v3.0.0-rc.8e — Order tracking inline (déclenché par intent "où est ma commande").
        if (b.type === "order_tracking_ref" && b.order_id) {
          return (
            <div key={`otr-${idx}-${b.order_id}`} data-testid={`order-tracking-ref-${b.order_id}`}>
              <OrderTrackingLive orderId={b.order_id} userId={b.user_id || null} />
            </div>
          );
        }
        // v3.0.0-rc.8c — Mini-cartes sponsorisées : carrousel horizontal compact
        // placé sous les fiches normales. Pied de liste.
        if (b.type === "sponsored_mini_row" && Array.isArray(b.chips) && b.chips.length > 0) {
          return (
            <div
              key={`spmr-${idx}`}
              data-testid="sponsored-mini-row"
              style={{ marginTop: 10 }}
            >
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.5, marginBottom: 6 }}>
                ✨ Sponsorisé — aussi disponibles
              </div>
              <div
                className="aya-sponsored-mini-scroll"
                style={{
                  display: "flex", gap: 8, overflowX: "auto",
                  padding: "4px 2px 8px",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                }}
              >
                {b.chips.map((c, i) => (
                  <button
                    key={c.id || `spm-${i}`}
                    type="button"
                    data-testid={`sponsored-mini-${c.id || i}`}
                    disabled={disabled}
                    onClick={() => onMerchantSelect && onMerchantSelect(
                      { action_id: c.action_id || "select_merchant", params: c.params || { merchant_id: c.id, merchant_name: c.name } },
                      { id: c.id, name: c.name, location: c.location }
                    )}
                    style={{
                      flex: "0 0 auto",
                      width: 180,
                      textAlign: "left",
                      padding: 8,
                      borderRadius: 10,
                      background: "rgba(232,213,163,0.10)",
                      border: "1px solid rgba(232,213,163,0.35)",
                      color: "inherit",
                      cursor: disabled ? "default" : "pointer",
                      display: "flex", flexDirection: "column", gap: 4,
                    }}
                  >
                    {c.image_url && (
                      <img src={c.image_url} alt=""
                        style={{ width: "100%", height: 70, objectFit: "cover", borderRadius: 6 }}
                        onError={(e) => { e.target.style.display = "none"; }} />
                    )}
                    <div style={{ fontWeight: 700, fontSize: 12.5, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7, display: "flex", justifyContent: "space-between" }}>
                      <span>{c.location || "—"}</span>
                      <span style={{ color: "#EBD9A8", fontWeight: 700 }}>★ {c.rating ? c.rating.toFixed(1) : "—"}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        }
        // Note : cta_row extrait dans ctaBlocks (rendu APRÈS carrousel produits).
        return null;
      })}
      {productBlocks.length > 0 && (
        <div
          ref={carouselRef}
          data-testid="product-carousel"
          className="aya-product-carousel"
          style={{
            display: "flex",
            gap: 10,
            overflowX: "auto",
            padding: "10px 2px 8px",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "thin",
            scrollSnapType: "x proximity",
          }}
        >
          {productBlocks.map((b, idx) => (
            <div key={b.id || `pc-${idx}`} className="aya-product-slide" style={{ scrollSnapAlign: "start", flex: "0 0 auto" }}>
              <ProductCard
                block={b}
                onAction={onProductAction}
                disabled={disabled}
                onOpenDetail={() => setDetailIndex(idx)}
              />
            </div>
          ))}
        </div>
      )}
      {detailIndex >= 0 && currentProductPack && (
        <ProductDetailModal
          products={currentProductPack}
          index={detailIndex}
          onIndexChange={setDetailIndex}
          onClose={closeDetail}
          onOrder={(p) => {
            const b = productBlocks[detailIndex];
            const action = (b?.actions || []).find((a) => a.action_id === "order_product");
            if (action && onProductAction) onProductAction(action, b);
          }}
          onViewShop={((productBlocks[detailIndex]?.actions || []).find((a) => a.action_id === "view_shop"))
            ? (p) => {
                const b = productBlocks[detailIndex];
                const action = (b?.actions || []).find((a) => a.action_id === "view_shop");
                if (action && onProductAction) onProductAction(action, b);
              }
            : null}
        />
      )}
      {/* v3.0.0-rc.8c — CTAs de suite rendus toujours EN DERNIER, après la grille. */}
      {ctaBlocks.map((b, idx) => (
        <div
          key={`cta-${idx}`}
          data-testid="cta-row"
          style={{
            display: "flex", gap: 8, flexWrap: "wrap",
            marginTop: 12, paddingTop: 10,
            borderTop: "1px dashed rgba(232,213,163,0.25)",
          }}
        >
          {(b.chips || []).map((c, i) => (
            <button
              key={`cta-chip-${idx}-${i}`}
              type="button"
              data-testid={`cta-chip-${idx}-${i}`}
              disabled={disabled}
              onClick={() => onQuickPrompt && onQuickPrompt(c.prompt || c.label)}
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                background: "linear-gradient(135deg, rgba(255,126,27,0.14), rgba(245,158,11,0.10))",
                border: "1px solid rgba(255,126,27,0.45)",
                color: "#FF7E1B",
                fontWeight: 700,
                fontSize: 12.5,
                cursor: disabled ? "default" : "pointer",
                transition: "transform 120ms ease, background 120ms ease",
              }}
              onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            >{c.label}</button>
          ))}
        </div>
      ))}
    </div>
  );
}
